import React, { useEffect, useState } from 'react';
import {
  Building2, Users, Bell, Plus, Trash2, Save, Loader2, Shield, Mail, MessageCircle, Send, CheckCircle2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Loader } from '@/components/common/Feedback';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { ROLE_LABEL } from '@/lib/format';
import { toast } from 'sonner';

const ROLES = ['project_manager', 'owner', 'foreman', 'accountant'];
const PREFS = [
  { key: 'new_detection', label: 'New potential change detected' },
  { key: 'high_value_only', label: 'High-confidence / high-value only' },
  { key: 'at_risk_alert', label: 'Change order sitting un-actioned (recovery at risk)' },
  { key: 'status_changes', label: 'Status changes & approvals' },
];

export default function Settings() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingC, setSavingC] = useState(false);

  const loadUsers = () => api.get('/users').then((r) => setUsers(r.data));
  useEffect(() => {
    Promise.all([api.get('/company').then((r) => setCompany(r.data)), loadUsers()]).finally(() => setLoading(false));
  }, []);

  const saveCompany = async () => {
    setSavingC(true);
    try {
      const r = await api.patch('/company', { name: company.name, notification_prefs: company.notification_prefs });
      setCompany(r.data); toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    finally { setSavingC(false); }
  };

  // --- Delivery-channel helpers ---
  const channels = company?.notification_prefs?.channels || {};
  const setChannel = (key, patch) => setCompany((c) => ({
    ...c,
    notification_prefs: {
      ...c.notification_prefs,
      channels: {
        ...(c.notification_prefs?.channels || {}),
        [key]: { ...((c.notification_prefs?.channels || {})[key] || {}), ...patch },
      },
    },
  }));

  const sendTest = async (channel) => {
    try {
      const r = await api.post('/messaging/test', { channel });
      const status = r.data?.result?.status;
      if (status === 'simulated') {
        toast.info(`Test ${channel} recorded (simulated) — enable the provider to send for real.`);
      } else if (status === 'sent') {
        toast.success(`Test ${channel} sent.`);
      } else {
        toast.warning(`Test ${channel}: ${status || 'unknown'}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not send test ${channel}`);
    }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); await loadUsers(); toast.success('Role updated'); }
    catch { toast.error('Update failed'); }
  };
  const removeUser = async (id) => {
    try { await api.delete(`/users/${id}`); await loadUsers(); toast('User removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  if (loading || !company) return <Loader label="Loading settings..." />;

  return (
    <div className="space-y-5 max-w-4xl">
      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="h-4 w-4 mr-1.5" />Company</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-1.5" />Users &amp; Roles</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell className="h-4 w-4 mr-1.5" />Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card className="p-5 space-y-4">
            <div className="max-w-md">
              <Label>Company name</Label>
              <Input className="mt-1" value={company.name || ''} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="company-name" />
            </div>
            <Button onClick={saveCompany} disabled={savingC} className="bg-primary hover:bg-ink-700" data-testid="save-company">
              {savingC ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-800">Team members</span>
              <InviteUserDialog onDone={loadUsers} />
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2 w-52">Role</th><th className="w-10" /></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{u.name}{u.id === user.id && <span className="ml-2 text-[10px] text-slate-400">(you)</span>}</td>
                    <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                    <td className="px-4 py-2.5">
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                        <SelectTrigger className="h-8" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 text-right">
                      {u.id !== user.id && <button onClick={() => removeUser(u.id)} className="text-slate-300 hover:text-red-500" data-testid={`del-user-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4 space-y-4">
          <Card className="p-5 space-y-1 max-w-xl">
            <div className="text-sm font-semibold text-slate-800 mb-1">Alert types</div>
            <p className="text-xs text-slate-500 mb-2">Choose which events create alerts. These fan out to every enabled channel below.</p>
            {PREFS.map((p) => (
              <div key={p.key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-700">{p.label}</span>
                <Switch checked={!!company.notification_prefs?.[p.key]} data-testid={`pref-${p.key}`}
                  onCheckedChange={(v) => setCompany({ ...company, notification_prefs: { ...company.notification_prefs, [p.key]: v } })} />
              </div>
            ))}
          </Card>

          <Card className="p-5 space-y-4 max-w-xl" data-testid="delivery-channels">
            <div>
              <div className="text-sm font-semibold text-slate-800">Delivery channels</div>
              <p className="text-xs text-slate-500 mt-0.5">In-app alerts are always on. Add email or WhatsApp to forward them to your team.</p>
            </div>

            <ChannelRow
              icon={<Mail className="h-4 w-4 text-brand" />}
              title="Email" placeholder="alerts@yourco.com, pm@yourco.com"
              status={company.messaging_status?.email}
              channel={channels.email || {}}
              onToggle={(v) => setChannel('email', { enabled: v })}
              onRecipients={(arr) => setChannel('email', { recipients: arr })}
              onTest={() => sendTest('email')}
              testid="channel-email"
            />

            <ChannelRow
              icon={<MessageCircle className="h-4 w-4 text-money" />}
              title="WhatsApp" placeholder="+15551234567, +447700900000"
              hint="Numbers in E.164 format (with country code)."
              status={company.messaging_status?.whatsapp}
              channel={channels.whatsapp || {}}
              onToggle={(v) => setChannel('whatsapp', { enabled: v })}
              onRecipients={(arr) => setChannel('whatsapp', { recipients: arr })}
              onTest={() => sendTest('whatsapp')}
              testid="channel-whatsapp"
            />

            <div className="pt-1">
              <Button onClick={saveCompany} disabled={savingC} className="bg-primary hover:bg-ink-700" data-testid="save-prefs">
                {savingC ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save preferences
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatusPill = ({ status }) => {
  if (!status) return null;
  let label = 'Not configured', cls = 'bg-slate-100 text-slate-500 border-slate-200';
  if (status.live) { label = 'Live'; cls = 'bg-money-50 text-money-700 border-money-200'; }
  else if (status.configured && !status.enabled) { label = 'Configured — flag off'; cls = 'bg-amber-50 text-amber-700 border-amber-200'; }
  else if (status.enabled && !status.configured) { label = 'Enabled — add credentials'; cls = 'bg-amber-50 text-amber-700 border-amber-200'; }
  return <Badge variant="outline" className={`text-[10px] ${cls}`} data-testid="channel-status">{status.live && <CheckCircle2 className="h-3 w-3 mr-1" />}{label}</Badge>;
};

const ChannelRow = ({ icon, title, placeholder, hint, status, channel, onToggle, onRecipients, onTest, testid }) => {
  const recipients = channel.recipients || [];
  return (
    <div className="rounded-lg border border-slate-200 p-3.5 space-y-2.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-slate-800">{title}</span>
          <StatusPill status={status} />
        </div>
        <Switch checked={!!channel.enabled} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
      </div>
      {channel.enabled && (
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-500">Recipients</Label>
          <Input
            className="h-8"
            placeholder={placeholder}
            defaultValue={recipients.join(', ')}
            onBlur={(e) => onRecipients(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            data-testid={`${testid}-recipients`}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400">{hint || 'Comma-separated. Saved when you click Save preferences.'}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onTest} data-testid={`${testid}-test`}>
              <Send className="h-3 w-3 mr-1" />Send test
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const InviteUserDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'project_manager' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!form.name || !form.email || !form.password) { toast.error('Fill all fields'); return; }
    setLoading(true);
    try {
      await api.post('/users', form);
      toast.success('User added'); setOpen(false); setForm({ name: '', email: '', password: '', role: 'project_manager' });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-primary hover:bg-ink-700 h-8" data-testid="invite-user-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add user</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
          <div><Label>Email</Label><Input type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
          <div><Label>Temporary password</Label><Input className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
          <div><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={loading} className="bg-money hover:bg-money-700" data-testid="new-user-submit">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add user</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
